import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type ApiProject } from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePages } from "../hooks/usePages";
import { ReviewSurface } from "../components/ReviewSurface";
import { PagesSidebar } from "../components/PagesSidebar";
import { InviteesPanel } from "../components/InviteesPanel";
import { SidebarToggle } from "../components/SidebarToggle";

// Persistent across sessions so an owner who routinely works on a small
// screen doesn't have to collapse the left rail on every visit.
const PAGES_HIDDEN_STORAGE_KEY = "clarity_pages_hidden";

function loadPagesHidden(): boolean {
  try {
    return localStorage.getItem(PAGES_HIDDEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function savePagesHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(PAGES_HIDDEN_STORAGE_KEY, "1");
    else localStorage.removeItem(PAGES_HIDDEN_STORAGE_KEY);
  } catch {
    // Storage may be unavailable (private mode, sandboxed iframe). Falling
    // back to in-memory state is fine — the toggle still works for the
    // current session, just doesn't survive a reload.
  }
}

type LoadState =
  | { status: "loading" }
  | { status: "ready"; project: ApiProject }
  | { status: "missing" }
  | { status: "error" };

type RefreshState =
  | { kind: "idle" }
  | { kind: "busy" }
  | { kind: "done"; added: number; skipped: number; status: "ok" | "partial" | "failed" }
  | { kind: "error" };

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [copied, setCopied] = useState(false);
  const [refresh, setRefresh] = useState<RefreshState>({ kind: "idle" });
  const [pagesHidden, setPagesHiddenState] = useState<boolean>(loadPagesHidden);
  const [currentPath, setCurrentPath] = useState<string>("");

  const setPagesHidden = useCallback((next: boolean) => {
    setPagesHiddenState(next);
    savePagesHidden(next);
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setState({ status: "loading" });
    try {
      const res = await api.getProject(id);
      setState({ status: "ready", project: res.project });
    } catch (err) {
      const status = (err as { status?: number }).status;
      setState(status === 404 ? { status: "missing" } : { status: "error" });
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // usePages must be called unconditionally before any early return so the
  // hook order stays stable across renders. It uses an empty projectId
  // string while the project is still loading; the hook tolerates that.
  const pages = usePages(
    state.status === "ready"
      ? { mode: "owner", projectId: state.project.id }
      : { mode: "owner", projectId: "" },
  );

  if (state.status === "loading") return <Centered>Loading…</Centered>;
  if (state.status === "missing")
    return (
      <Centered>
        <p>That project doesn’t exist or was archived.</p>
        <Link to="/dashboard" className="mt-3 inline-block text-sm underline">
          Back to dashboard
        </Link>
      </Centered>
    );
  if (state.status === "error") return <Centered>Something went wrong loading this project.</Centered>;
  if (authState.status !== "authed") return null;

  const project = state.project;
  const shareUrl = `${window.location.origin}/p/${project.shareToken}`;

  async function copyShare() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  async function rotateShare() {
    if (
      !confirm(
        "Generate a new share link? Anyone using the current link will lose access immediately.",
      )
    ) {
      return;
    }
    const res = await api.rotateShareToken(project.id);
    setState({ status: "ready", project: res.project });
  }

  async function archive() {
    if (!confirm("Archive this project? The share link will stop working.")) return;
    await api.archiveProject(project.id);
    navigate("/dashboard", { replace: true });
  }

  async function refreshPages() {
    if (refresh.kind === "busy") return;
    setRefresh({ kind: "busy" });
    try {
      const res = await api.rediscoverPages(project.id);
      setRefresh({
        kind: "done",
        added: res.added.length,
        skipped: res.skipped,
        status: res.status,
      });
      void pages.reload();
      setTimeout(() => {
        setRefresh((current) => (current.kind === "done" ? { kind: "idle" } : current));
      }, 4000);
    } catch {
      setRefresh({ kind: "error" });
      setTimeout(() => {
        setRefresh((current) => (current.kind === "error" ? { kind: "idle" } : current));
      }, 4000);
    }
  }

  const showLeftSidebar = !pagesHidden;
  const showFloatingToggle = pagesHidden;

  return (
    <section className="flex h-[calc(100vh-3.5rem)]">
      {showLeftSidebar ? (
        <div className="flex w-72 shrink-0 flex-col overflow-y-auto border-r border-neutral-200 bg-neutral-50 px-5 py-6">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="text-xs text-neutral-500 hover:text-neutral-900">
              ← Dashboard
            </Link>
            <SidebarToggle
              hidden={false}
              onToggle={() => setPagesHidden(true)}
              variant="edge"
            />
          </div>
          <h1 className="mt-3 truncate text-base font-semibold">{project.name}</h1>
          <a
            href={project.targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 block truncate text-xs text-neutral-500 hover:underline"
          >
            {project.targetUrl}
          </a>

          <div className="mt-6">
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
              Share link
            </div>
            <div className="mt-2 flex gap-2">
              <input
                readOnly
                value={shareUrl}
                className="flex-1 rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-xs"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                onClick={() => void copyShare()}
                className="rounded-md bg-neutral-900 px-3 text-xs text-white hover:bg-neutral-700"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button
              type="button"
              onClick={() => void rotateShare()}
              className="mt-2 text-xs text-neutral-500 hover:text-neutral-900"
            >
              Generate new link
            </button>
          </div>

          <div className="mt-6">
            <PagesSidebar
              pages={pages.pages}
              activePath={currentPath || null}
              mode="owner"
              onSelect={(page) => setCurrentPath(page.path)}
              onAddPage={pages.create}
              onRenamePage={pages.rename}
              onDeletePage={pages.remove}
              onReorderPages={pages.reorder}
              onSetPageVisibility={pages.setVisibility}
            />
            <button
              type="button"
              onClick={() => void refreshPages()}
              disabled={refresh.kind === "busy"}
              className="mt-3 rounded-md border border-neutral-300 px-3 py-1.5 text-xs hover:bg-neutral-100 disabled:opacity-50"
            >
              {refresh.kind === "busy" ? "Refreshing…" : "Refresh pages"}
            </button>
            <RefreshFeedback state={refresh} />
          </div>

          <div className="mt-6">
            <InviteesPanel projectId={project.id} />
          </div>

          <div className="mt-6 space-y-1.5">
            <Link
              to={`/projects/${project.id}/styleguide`}
              className="block text-xs text-neutral-600 hover:text-neutral-900"
            >
              Styleguide →
            </Link>
            <Link
              to={`/projects/${project.id}/elements`}
              className="block text-xs text-neutral-600 hover:text-neutral-900"
            >
              Element guide →
            </Link>
          </div>

          <div className="mt-auto border-t border-neutral-200 pt-4">
            <button
              type="button"
              onClick={() => void archive()}
              className="text-xs text-red-600 hover:underline"
            >
              Archive project
            </button>
          </div>
        </div>
      ) : null}

      <div className="relative min-w-0 flex-1">
        {showFloatingToggle ? (
          <SidebarToggle
            hidden={true}
            onToggle={() => setPagesHidden(false)}
            variant="floating"
          />
        ) : null}
        <ReviewSurface
          mode="owner"
          projectId={project.id}
          shareToken={project.shareToken}
          defaultIdentity={{ name: authState.user.name, email: authState.user.email }}
          pages={pages.pages}
          currentPath={currentPath}
          onSetCurrentPath={setCurrentPath}
          onCommentsChanged={pages.reload}
        />
      </div>
    </section>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-6 py-20 text-center text-neutral-600">{children}</div>
  );
}

function RefreshFeedback({ state }: { state: RefreshState }) {
  if (state.kind === "idle" || state.kind === "busy") return null;
  if (state.kind === "error") {
    return <p className="mt-2 text-xs text-red-600">Couldn’t refresh — try again.</p>;
  }
  if (state.added === 0) {
    return <p className="mt-2 text-xs text-neutral-500">No new pages found.</p>;
  }
  const label = state.added === 1 ? "Added 1 new page" : `Added ${state.added} new pages`;
  const note =
    state.status === "partial" ? " (capped at 30 — re-run if you expect more)" : "";
  return <p className="mt-2 text-xs text-neutral-600">{label}{note}.</p>;
}
